<template>
    <div>
        <sau-template :retrieved="gotData">
            <!-- Fetching Placeholder -->
            <template slot="message">Fetching Current Status</template>

            <!-- Data -->
            <template slot="data">
                <template v-if="processing">
                    <div class="row">
                        <div class="col">
                            <div class="sau-bx bx-pad text-center">
                                <h3>{{ actions[action.toLowerCase()] }}</h3>
                                <img src="/img/spinner-dark-gray-70.gif" alt="spinner" class="m-6"/>
                                <p>Please wait for the current action to finish before interacting with your server</p>
                                <p>Navigating away from this page will not disrupt the current action, however, reloading the page may display a temporary status while the action completes.</p>
                            </div>
                        </div>
                    </div>
                </template>

                <template v-else-if="!provisioned">
                    <div class="row">
                        <div class="col">
                            <div class="sau-bx bx-pad">
                                <h3>Your Server is still being Provisioned</h3>
                                <p>Server Provisioning usually takes 5-45 minutes. If it has taken longer than this, our support team will be investigating the issue.</p>
                                <p>If you would like assistance from one of our support staff please click the link below.</p>
                                <sau-utility-support></sau-utility-support>
                            </div>
                        </div>
                    </div>
                </template>
                <template v-else-if="!support">
                    <!-- show available power options -->
                    <template v-if="! displayRebuildOption">
                        <div class="row">
                            <div class="col">
                                <h3>Power &amp; Actions</h3>
                            </div>
                        </div>

                        <div class="row">
                            <div class="col-lg-6 pb-6">
                                <button
                                    class="sau-power lg"
                                    :class="powerCss('on')"
                                    @click.prevent="performAction('on')"
                                    :disabled="deviceOn"
                                >
                                    <i class="far fa-power-off pt-2" aria-hidden="true"></i>
                                    <p class="pt-2">
                                        Power On
                                        <span
                                            v-if="deviceOn"
                                            class="sau-help sm ml-2 align-middle"
                                            v-b-tooltip.hover
                                            :title="on.start"
                                        >?</span>
                                        <span
                                            v-else
                                            class="sau-help sm ml-2 align-middle"
                                            v-b-tooltip.hover
                                            :title="off.start"
                                        >?</span>
                                    </p>
                                </button>
                            </div>

                            <div class="col-lg-6 pb-6">
                                <button
                                    class="sau-power lg"
                                    :class="powerCss('off')"
                                    @click.prevent="performAction('off')"
                                    :disabled="!deviceOn"
                                >
                                    <i class="far fa-power-off pt-2" aria-hidden="true"></i>
                                    <p class="pt-2">
                                        Power Off
                                        <span
                                            v-if="!deviceOn"
                                            class="sau-help sm ml-2 align-middle"
                                            v-b-tooltip.hover
                                            :title="off.stop"
                                        >?</span>
                                        <span
                                            v-else
                                            class="sau-help sm ml-2 align-middle"
                                            v-b-tooltip.hover
                                            :title="on.stop"
                                        >?</span>
                                    </p>
                                </button>
                            </div>
                        </div>

                        <div class="row pt-4">
                            <div class="col-lg-4 drop mb-6">
                                <button
                                    @click.prevent="performAction('reset')"
                                    class="sau-power sm"
                                    :class="action === 'reset' ? 'yellow':'muted'"
                                    :disabled="!availableActions('reset')"
                                >
                                    <i class="far fa-redo" aria-hidden="true"></i>
                                    <p class="pt-2">
                                        Reset
                                        <span
                                            class="sau-help sm ml-2 align-middle"
                                            v-b-tooltip.hover
                                            :title="messages.reset"
                                        >?</span>
                                    </p>
                                </button>
                            </div>
                            <div class="col-lg-4 drop mb-6">
                                <button
                                    @click.prevent="performAction('cycle')"
                                    class="sau-power sm"
                                    :class="action === 'cycle' ? 'yellow':'muted'"
                                    :disabled="!availableActions('cycle')"
                                >
                                    <i class="far fa-sync-alt" aria-hidden="true"></i>
                                    <p class="pt-2">
                                        Power Cycle
                                        <span
                                            class="sau-help sm ml-2 align-middle"
                                            v-b-tooltip.hover
                                            :title="messages.cycle"
                                        >?</span>
                                    </p>
                                </button>
                            </div>
                            <div class="col-lg-4 mb-6">
                                <button
                                    @click.prevent="performAction('force')"
                                    href="#"
                                    class="sau-power sm"
                                    :class="action === 'force' ? 'yellow':'muted'"
                                    :disabled="!availableActions('force')"
                                >
                                    <i class="far fa-plug" aria-hidden="true"></i>
                                    <p class="pt-2">
                                        Force Power Off
                                        <span
                                            class="sau-help sm ml-2 align-middle"
                                            v-b-tooltip.hover
                                            :title="messages.force"
                                        >?</span>
                                    </p>
                                </button>
                            </div>
                            <div class="col-lg-4 mb-6" v-if="supportsRebuild">
                                <button
                                    @click="displayRebuildOption = true"
                                    class="sau-power sm"
                                    :class="action === 'force' ? 'yellow':'muted'"
                                    :disabled="!availableActions('force')"
                                >
                                    <i class="far fa-step-backward" aria-hidden="true"></i>
                                    <p class="pt-2">
                                        Rebuild
                                    </p>
                                </button>
                            </div>
                        </div>
                    </template>

                    <!-- Rebuild -->
                    <div class="container" v-if="displayRebuildOption">
                        <div class="row">
                            <div class="col text-center">
                                <p class="mb-2"><i class="fas fa-step-backward success text-4xl"></i></p>
                                <h5>Rebuild Server</h5>
                            </div>
                        </div>
                        <div class="row border-2 border-grey-200 bg-grey-100 py-5">
                            <div class="col-12 col-md-6">
                                <div class="flex flex-col md:flex-row flex-wrap text-center md:text-left">
                                    <p>Rebuilding your server will <strong>permanently delete</strong> all of the data on your server and will perform a fresh installation of the selected operating system.</p>
                                </div>
                            </div>
                            <div class="col-12 col-md-6">
                                <div class="d-flex flex-col md:flex-row md:items-end md:justify-end items-center flex-wrap">
                                    <div class="mb-3 lg:mb-0" style="min-width:50%">
                                        <label for="inputOs" class="d-block font-bold">Operating System</label>
                                        <select v-model="selectedRebuildOs" id="inputOs" class="form-control">
                                            <option value="" selected disabled></option>
                                            <option v-for="(os, index) in availableRebuildOperatingSystems" :key="`os-${index}`" :value="os">{{ os.version }}</option>
                                        </select>
                                    </div>
                                    <div>
                                        <button @click="confirmRebuild" class="btn btn-success font-bold btn-sm text-uppercase ml-3" :disabled="selectedRebuildOs === null || selectedRebuildOs == '' || requestingRebuild === true">
                                            Rebuild Server <i class="far fa-spinner-third fa-spin" v-show="requestingRebuild"></i>
                                        </button>
                                    </div>
                                    <button @click="displayRebuildOption = false" class="btn btn-light font-bold btn-sm text-uppercase mt-2">Cancel</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>

                <template v-else>
                    <!-- show support form -->
                    <div class="row">
                        <div class="col">
                            <div class="sau-bx bx-pad">
                                <h3>We are having issues accessing your Server!</h3>
                                <p>No response has been received since the requested action was performed. This is usually a temporary issue.</p>
                                <p>If you would like assistance from one of our support staff please click the link below.</p>
                                <sau-utility-support></sau-utility-support>
                            </div>
                        </div>
                    </div>
                </template>
            </template>
        </sau-template>
    </div>
</template>

<script>
import { setInterval, clearInterval, clearTimeout, setTimeout } from 'timers';
export default {
    props: ['service'],
    data() {
        return {
            deviceOn: '',
            id: '',
            support: false,
            processing: false,
            provisioned: false,
            poll: '',
            action: '',
            supportsRebuild: false,
            safeWord: 'REBUILD',
            safeWordConfirmation: '',
            displayRebuildOption: false,
            selectedRebuildOs: null,
            availableRebuildOperatingSystems: [],
            requestingRebuild: false,
        };
    },
    created() {
        this.startApp();
    },
    methods: {
        startApp() {
            this.getDeviceStatus().then(response => {
                if (response.data.status == 'success') {
                    this.deviceOn =
                        response.data.powerstate != 'on' ? false : true;
                    this.support = false;
                    this.provisioned = true;
                } else if (
                    response.data.status == 'error' &&
                    response.data.not_provisioned
                ) {
                    this.support = false;
                    this.provisioned = false;
                }
            });

            this.getRequest(`/api/v2/services/${this.getServiceId()}/rebuildOptions`)
                .then((response) => {
                    if(response.data.length === 0) {
                        this.supportsRebuild = false;
                        return;
                    }
                    this.supportsRebuild = true;
                    this.availableRebuildOperatingSystems = response.data;
                }).catch((error) => {
                    this.supportsRebuild = false;
                })
        },
        checkServerResult() {
            /** Called after performing an action on the server
             * - compare against last action
             * So if action = power off, wait till we get a 'off' powerstatus response (not on) etc
             */
            this.getDeviceStatus()
                .then(response => {
                    if ((response.data.status = 'success')) {
                        // Compare the response against the action we requested
                        this.deviceOn =
                            response.data.powerstate != 'on' ? false : true;
                        switch (this.action.toLowerCase()) {
                            case 'on':
                            case 'reset':
                            case 'cycle':
                                // Need an 'on' response'
                                if (response.data.powerstate == 'on') {
                                    this.cancelPolling();
                                }
                                break;
                            case 'off':
                            case 'force':
                                // Need a 'off' response
                                if (response.data.powerstate == 'off') {
                                    this.cancelPolling();
                                }
                                break;
                            default:
                                console.log(
                                    'Unknown remote management action: ' +
                                        this.action
                                );
                                break;
                        }
                    }
                })
                .catch(error => {
                    this.support = true;
                });
        },
        powerCss(state) {
            switch (state) {
                case 'on':
                    return this.action
                        ? 'muted'
                        : this.deviceOn
                        ? 'green'
                        : 'muted';
                    break;
                case 'off':
                    return this.action
                        ? 'muted'
                        : this.deviceOn
                        ? 'muted'
                        : 'red';
                    break;
            }
        },
        cancelPolling() {
            if (this.poll) {
                if (this.action == 'reset' || this.action == 'cycle') {
                    clearTimeout(this.poll);
                } else {
                    clearInterval(this.poll);
                }
                this.poll = null;
                this.processing = false;
                this.action = '';
            }
        },
        performAction(action) {
            let msg = '';
            let title = '';
            let actionConfirmButtonClass = 'btn btn-danger';
            switch (action) {
                case 'reset':
                    msg = this.messages.reset;
                    title = 'Yes, reset it!';
                    break;
                case 'cycle':
                    msg = this.messages.cycle;
                    title = 'Yes, power cycle it!';
                    break;
                case 'force':
                    msg = this.messages.force;
                    title = 'Yes, force power off!';
                    break;
                case 'on':
                    msg = this.messages.on;
                    title = 'Yes, power it on!';
                    actionConfirmButtonClass = 'btn btn-success';
                    break;
                case 'off':
                    msg = this.messages.off;
                    title = 'Yes, power it off!';
                    break;
            }
            this.$swal.queue([
                {
                    title: 'Are you sure?',
                    text: msg,
                    type: 'warning',
                    showCancelButton: true,
                    confirmButtonText: title,
                    confirmButtonClass: actionConfirmButtonClass,
                    cancelButtonText: 'No, cancel',
                    cancelButtonClass: 'btn btn-link',
                    buttonsStyling: false,
                    reverseButtons: true,
                    preConfirm: () => {
                        return axios
                            .post(
                                '/api/services/' +
                                    this.getServiceType() +
                                    '/' +
                                    this.getServiceId() +
                                    '/' +
                                    action,
                                {}
                            )
                            .then(response => {
                                if (response.data.status === 'success') {
                                    this.processing = true;
                                    this.action = action;
                                    // Set timeout or interval depending on action taken
                                    if (
                                        action == 'reset' ||
                                        action == 'cycle'
                                    ) {
                                        // Set timeout - tried waiting for an 'off' then 'on' status but it always remains 'on' so just flag as succeeded in 20 seconds
                                        this.poll = setTimeout(
                                            function() {
                                                this.checkServerResult();
                                            }.bind(this),
                                            20000
                                        );
                                    } else {
                                        // Set timeout to poll server power to see when the requested action has completed
                                        this.poll = setInterval(
                                            function() {
                                                this.checkServerResult();
                                            }.bind(this),
                                            3000
                                        );
                                    }
                                } else {
                                    this.$swal.insertQueueStep({
                                        type: 'error',
                                        title: 'There seems to be a problem',
                                        html:
                                            'There was an issue trying to perform the requested action. Please try again shortly.'
                                    });
                                    this.processing = false;
                                    this.support = true;
                                }
                            })
                            .catch(error => {
                                this.$swal.insertQueueStep({
                                    type: 'error',
                                    title: 'There seems to be a problem',
                                    html:
                                        'There was an issue trying to perform the requested action. Please try again shortly.'
                                });
                                this.processing = false;
                                this.support = true;
                            });
                    }
                }
            ]);
        },
        availableActions(action) {
            switch (action) {
                case 'on':
                    return this.deviceOn ? false : true;
                    break;
                case 'reset':
                case 'cycle':
                case 'force':
                case 'soft':
                case 'off':
                    return this.deviceOn ? true : false;
                    break;
            }
        },

        confirmRebuild() {
            this.$swal({
                title: 'Confirm Rebuild',
                input: 'text',
                type: 'warning',
                html: `
                    <p>You are about to rebuild your server with the Operating System ${this.selectedRebuildOs.version}.</p>
                    <p>To confirm, please enter <strong class="sau-font-bold">${this.safeWord}</strong> into the text box below, and click 'Rebuild Server'</p>`,
                inputAttributes: {
                    autocapitalize: 'on'
                },
                showCancelButton: true,
                confirmButtonText: 'Rebuild Server',
                customContainerClass: 'default-style-swal',
                inputValidator: (value) => {
                    if(value != this.safeWord) {
                        return `Please type ${this.safeWord} into the text box above. This is case sensitive.`;
                    }
                },
                allowOutsideClick: false,
            }).then((result) => {
                if (result.value === this.safeWord) {

                    this.requestingRebuild = true;

                    this.postRequest(`/api/v2/services/${this.getServiceId()}/rebuild`, { operating_system: this.selectedRebuildOs.id, sau_order_id: this.service.sau_order_id })
                        .then((response) => {
                            this.$swal({
                                type: 'success',
                                title: 'Confirmed!',
                                text: 'A rebuild has been scheduled for your server.',
                                customContainerClass: 'default-style-swal'
                            })
                        }).catch((error) => {
                            this.$swal({
                                type: 'error',
                                title: `There was an error rebuilding the server.`,
                                html: `<p>${error.response.data.message}</p><p>Please contact support to complete this process.</p>`,
                                showCancelButton: true,
                                showConfirmButton: true,
                                confirmButtonText: 'Contact Support',
                                customContainerClass: 'default-style-swal',
                            }).then((result) => {
                                // Confirmed to want to talk to support
                                if(result.value) {
                                    window.location.href = '/support';
                                }
                            })
                        }).finally(() => {
                            this.requestingRebuild = false;
                        })
                }
            })

        },

        capitalizeInput() {
            this.safeWordConfirmation = this.safeWordConfirmation.toUpperCase();
        },
    },
    computed: {
        safeWordConfirmed() {
            return this.safeWord === this.safeWordConfirmation;
        },

    }
};
</script>

<style>
.myactive {
    background-color: red;
}
button:disabled {
    cursor: not-allowed;
}
.muted:hover {
    color: #e5e5e5 !important;
    border: 1px solid #e5e5e5 !important;
}
.muted:hover i {
    color: #e5e5e5 !important;
}
.muted:not([disabled]) {
    color: #666666 !important;
    border: 1px solid #666666 !important;
}
.muted:not([disabled]) i {
    color: #666666 !important;
}
.swal2-actions button {
    text-transform: uppercase;
    font-weight: 600 !important;
}
</style>
